/* eslint-disable no-undef */
import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';

import { DateRangePicker as RsuiteDateRangePicker, } from 'rsuite';
import 'rsuite/DateRangePicker/styles/index.css';

import "./DateRangePicker.styles.sass";

type ValueType = [Date, Date];

export const DateRangePicker = ({defaultValue, onChange}: {defaultValue: ValueType, onChange: (value: ValueType) => void}) => {
  const [value, setValue] = useState<ValueType>(defaultValue);
  const [inputValue, setInputValue] = useState<string>(moment(value[0]).format('YYYY-MM-DD') + ' - ' + moment(value[1]).format('YYYY-MM-DD'));
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);
  const inputRef = useRef(null);
  const [error, setError] = useState(null);

  const handleChange = (value: string) => {
    const [from, to] = value.split(' - ');

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setInputValue(moment(from).format('YYYY-MM-DD') + ' - ' + moment(to).format('YYYY-MM-DD'));
      setValueHandler([from, to || '']);
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const setValueHandler = (value: ValueType | [string, string]) => {
    const from = moment(value[0]);
    const to = moment(value[1]);

    if (!from.isValid() || !to.isValid()) {
      setError('Please enter a valid date');
      return;
    }

    if (from.isAfter(to)) {
      setError('"From" date cannot be after "To" date');
      return;
    }

    if (error) setError(null);

    const newValue: ValueType = [from.toDate(), to.toDate()];

    setValue(() => newValue);
    onChange(newValue);
  };


  return (
    <div className="dateRangePicker">
      <button
        className='arrowButton'
        onClick={() => {
          const from: Date = value[0];
          const to: Date = value[1];
          const newValue: ValueType = [moment(from).subtract(1, 'day').toDate(), moment(to).subtract(1, 'day').toDate()];

          setInputValue(moment(newValue[0]).format('YYYY-MM-DD') + ' - ' + moment(newValue[1]).format('YYYY-MM-DD'));
          setValueHandler(newValue);
        }}
      ><i className="fas fa-chevron-left"></i></button>
      <div className="inputWrapper">
        <input
          ref={inputRef}
          className="input"
          type="text"
          value={inputValue}
          onKeyPress={(e: any) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onFocus={() => setOpen(true)}
          onBlur={(e: any) => {
            if (!e?.relatedTarget?.classList?.value.includes('rs-calendar')) {
              setOpen(false);
            }
          }}
          onChange={(value) => {
            setOpen(false);
            setInputValue(value.target.value);
            handleChange(value.target.value);
          }}
        />
        <i className="far fa-calendar-alt" aria-hidden="true"></i>
        {error && <span className="inputError">{error}</span>}
      </div>
      <button
        className='arrowButton'
        onClick={() => {
          const from: Date = value[0];
          const to: Date = value[1];
          const newValue: ValueType = [moment(from).add(1, 'day').toDate(), moment(to).add(1, 'day').toDate()];

          setInputValue(moment(newValue[0]).format('YYYY-MM-DD') + ' - ' + moment(newValue[1]).format('YYYY-MM-DD'));
          setValueHandler(newValue);
        }}
      ><i className="fas fa-chevron-right"></i></button>
      <div className='dateRangePickerRef'>
        <RsuiteDateRangePicker
          showHeader={false}
          isoWeek={true}
          character=' - '
          value={value}
          onChange={setValueHandler}
          placeholder="Pick a date"
          open={open}
          onOk={(value: ValueType) => {
            setInputValue(moment(value[0]).format('YYYY-MM-DD') + ' - ' + moment(value[1]).format('YYYY-MM-DD'));
          }}
          onSelect={() => inputRef?.current.focus()}
          onShortcutClick={({ value }: any) => {
            setInputValue(moment(value[0]).format('YYYY-MM-DD') + ' - ' + moment(value[1]).format('YYYY-MM-DD'));
          }}
          renderTitle={date => {
            const locale = localStorage.getItem('roedl-language') || 'en';
            moment.locale(locale);

            return moment(date).format('MMMM YYYY');
          }}
        />
      </div>
    </div>
  );
};
